import React from 'react';
import { TableHead, TableRow, TableCell, Box, Switch, Typography, TableSortLabel } from '@mui/material';
import { Header } from 'components/tables/common';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import { getCompleteCellClass } from 'components/tables/common/simpleTableClasses';
import { AuditTableHeaderProps } from '../../types';
import { AUDIT_TABLE_COLUMN_STYLES, getAuditColumnStyle } from './auditTableColumnStyles';

type MovementHeaderLabel = {
  operator?: 'add' | 'less';
  label: string;
};

const AuditTableHeader: React.FC<AuditTableHeaderProps> = ({
  selectedStore,
  conditionalBorderColumns,
  columnConfig,
  reconciliationColumnIndex,
  showNetVariance = true,
  isExpandAllChecked,
  onExpandAllChange,
  hideStockColumns = false,
  showBackstockAccounting = false,
  onToggleBackstockAccounting,
  dateSortDirection = 'desc',
  onDateSortChange
}) => {
  // Check if lotto is authorized but POS is not connected
  const isBothConnected = selectedStore?.pos_type && selectedStore?.pos_type !== "n/a" && selectedStore?.lotto_authorized;
  const isBothNotConnected = (!selectedStore?.pos_type || selectedStore?.pos_type === "n/a") && (!selectedStore?.lotto_authorized);
  const isLottoConnectedOnly = selectedStore?.lotto_authorized && (!selectedStore?.pos_type || selectedStore?.pos_type === "n/a");
  const isPOSConnectedOnly = selectedStore?.pos_type && selectedStore?.pos_type !== "n/a" && !selectedStore?.lotto_authorized;
  const hasPosType = Boolean(selectedStore?.pos_type && selectedStore?.pos_type !== "n/a");
  const hasGroupedInventoryHeader = showBackstockAccounting;
  const totalColumnCount = reconciliationColumnIndex + (showNetVariance ? 1 : 0);
  const summaryColumnCount = showBackstockAccounting
    ? 0
    : isBothConnected || isPOSConnectedOnly ? 7 : isLottoConnectedOnly ? 4 : 2;
  const inventoryColumnCount = Math.max(0, columnConfig.length - summaryColumnCount);
  const summaryStartColumnIndex = inventoryColumnCount + 1;
  const withVarianceDollarRightBorder = (className: string) => `${className} audit-table__variance-dollar-right-border`;
  const dateColumnStyle = AUDIT_TABLE_COLUMN_STYLES.date;
  const backstockColumnLabels = ['Opening', 'Activated to Backstock', 'Counter to Backstock', 'Backstock to Counter', 'Returned to Lottery', 'Sold Out', 'Closing'];
  const counterColumnLabels = showBackstockAccounting
    ? ['Opening Tickets', 'Activated Tickets', 'From Backstock', 'To Backstock', 'Sold Out', 'Closing Tickets', 'Expected Sales']
    : ['Opening Tickets', 'Activated Tickets', 'Closing Tickets', 'Expected Sales'];
  const backstockMovementHeadings: Record<string, MovementHeaderLabel> = {
    Opening: { label: 'Opening' },
    'Activated to Backstock': { operator: 'add', label: 'Activated' },
    'Counter to Backstock': { operator: 'add', label: 'From Counter' },
    'Backstock to Counter': { operator: 'less', label: 'To Counter' },
    'Returned to Lottery': { operator: 'less', label: 'Returned to Lottery' },
    'Sold Out': { operator: 'less', label: 'Sold Out' },
    Closing: { label: 'Closing' },
  };
  const counterMovementHeadings: Record<string, MovementHeaderLabel> = {
    'Opening Tickets': { label: 'Opening' },
    'Activated Tickets': { operator: 'add', label: 'Activated' },
    'From Backstock': { operator: 'add', label: 'From Backstock' },
    'To Backstock': { operator: 'less', label: 'To Backstock' },
    'Sold Out': { operator: 'less', label: 'Sold Out' },
    'Closing Tickets': { operator: 'less', label: 'Closing' },
    'Expected Sales': { label: 'Expected Sales' },
  };
  const headerRowCount = hasGroupedInventoryHeader ? 3 : 2;
  const inventoryColumns = columnConfig.slice(0, inventoryColumnCount);
  const backstockStartIndex = inventoryColumns.findIndex((column) => column === backstockColumnLabels[0]);
  const backstockColumns = showBackstockAccounting && backstockStartIndex >= 0
    ? inventoryColumns.slice(backstockStartIndex, backstockStartIndex + backstockColumnLabels.length)
    : [];
  const counterStartIndex = showBackstockAccounting && backstockStartIndex >= 0
    ? backstockStartIndex + backstockColumns.length
    : inventoryColumns.findIndex((column) => counterColumnLabels.includes(column));
  const counterColumnCount = hideStockColumns ? 1 : counterColumnLabels.length;
  const counterColumns = counterStartIndex >= 0
    ? inventoryColumns.slice(counterStartIndex, counterStartIndex + counterColumnCount)
    : [];
  const columnsBeforeBackstock = showBackstockAccounting && backstockStartIndex > 0
    ? inventoryColumns.slice(0, backstockStartIndex)
    : [];
  const columnsBeforeCounter = !showBackstockAccounting && counterStartIndex > 0
    ? inventoryColumns.slice(0, counterStartIndex)
    : [];
  const columnsAfterCounter = counterStartIndex >= 0
    ? inventoryColumns.slice(counterStartIndex + counterColumns.length)
    : inventoryColumns.slice(showBackstockAccounting ? backstockStartIndex + backstockColumns.length : 0);
  const summaryColumns = columnConfig.slice(inventoryColumnCount);
  const inventoryGroupHeaderStyles = {
    backstock: {
      cell: { textAlign: 'center' as const },
      text: { margin: 0, color: '#8A5A2B', fontWeight: 600 },
    },
    counter: {
      cell: { textAlign: 'center' as const },
      text: { margin: 0 },
    },
  };

  const renderGroupLabel = (label: string, variant: keyof typeof inventoryGroupHeaderStyles) => (
    <Box className="audit-table__normal-subheader">
      <p
        className="table-header-text text-align-center"
        style={inventoryGroupHeaderStyles[variant].text}
      >
        {label}
      </p>
    </Box>
  );

  const renderToggle = (
    label: string,
    checked: boolean,
    onToggle?: () => void
  ) => (
    <Box
      component="label"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0,
        py: 0,
        color: checked ? '#1565C0' : '#475569',
        fontSize: 11,
        fontWeight: 400,
        cursor: 'pointer',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Switch
        size="small"
        checked={checked}
        onChange={(event) => {
          event.stopPropagation();
          onToggle?.();
        }}
        sx={{
          width: 28,
          height: 18,
          p: 0,
          '& .MuiSwitch-switchBase': {
            p: '2px',
            '&.Mui-checked': {
              transform: 'translateX(10px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#1976D2',
                opacity: 1,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: 14,
            height: 14,
          },
          '& .MuiSwitch-track': {
            borderRadius: 9,
            backgroundColor: '#94A3B8',
            opacity: 1,
          },
        }}
      />
      <span>{label}</span>
    </Box>
  );

  const renderHeaderTitle = (title: string, showInventoryToggles = false) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        minHeight: showInventoryToggles ? 46 : 30,
      }}
    >
      <p className="table-header-text text-align-center" style={{ margin: 0 }}>{title}</p>
      {showInventoryToggles && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {renderToggle('Show inventory movements', showBackstockAccounting, onToggleBackstockAccounting)}
        </Box>
      )}
    </Box>
  );


  // Render table cell for each column
  // Column indices: 0 (Date), then the grouped summary columns.
  const renderTableCell = (
    text: string,
    index: number,
    rowSpan?: number,
    options?: { isBackstockColumnHeading?: boolean }
  ) => {
    const isBackstockColumnHeading = Boolean(options?.isBackstockColumnHeading);
    const movementHeading = showBackstockAccounting
      ? isBackstockColumnHeading
        ? backstockMovementHeadings[text]
        : counterMovementHeadings[text]
      : undefined;
    // For lotto-only case, column indices are straightforward: index + 1 (Date)
    // For other cases, need to account for different column structures
    const baseOffset = 1; // Date (0)
    let colIdx = index + baseOffset;

    // Special handling for lotto-only case is not needed as column structure is linear
    // The complex logic was for old column structure

    const renderText = () => (
      <Box className="audit-table__normal-subheader audit-table__normal-subheader--right">
        <Typography
          variant="caption"
          className="table-subheader-text audit-table__normal-subheader-text"
          sx={isBackstockColumnHeading ? { fontWeight: 400, fontStyle: 'normal', color: '#8A5A2B' } : undefined}
        >
          {movementHeading ? (
            <span className="audit-table__movement-header-label">
              {movementHeading.operator && (
                <span className="audit-table__movement-header-operator">
                  {movementHeading.operator}
                </span>
              )}
              <span className="audit-table__movement-header-title">
                {movementHeading.label}
              </span>
            </span>
          ) : (
            text
          )}
        </Typography>
      </Box>
    );

    return <TableCell
      key={index}
      className={[
        getCompleteCellClass({
          rowIdx: "subheader",
          colIdx: colIdx,
          totalRows: headerRowCount,
          totalCols: totalColumnCount,
          borderColumns: conditionalBorderColumns
        }),
        isBackstockColumnHeading ? 'audit-table__inventory-subheader--backstock' : '',
        text === 'Variance' ? 'audit-table__variance-dollar-right-border' : '',
      ].filter(Boolean).join(' ')}
      rowSpan={rowSpan}
      style={{
        ...getAuditColumnStyle(text, showBackstockAccounting),
        textAlign: 'right',
      }}
    >
      {renderText()}
    </TableCell>
  };

  const renderBackstockAccountingHeader = () => (
    <>
      <TableRow>
        <TableCell
          rowSpan={3}
          className={getCompleteCellClass({ rowIdx: 0, colIdx: 0, totalRows: headerRowCount, totalCols: totalColumnCount })}
          style={{ ...dateColumnStyle, cursor: 'pointer' }}
          aria-sort={dateSortDirection === 'asc' ? 'ascending' : 'descending'}
          onClick={() => onDateSortChange?.()}
        >
          <div className="audit-table__date-header">
            <p className="table-header-text">
              <TableSortLabel
                active
                direction={dateSortDirection}
                hideSortIcon={false}
              >
                Date
              </TableSortLabel>
            </p>
            <label
              className="audit-table__expand-all-control"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onExpandAllChange(!isExpandAllChecked);
              }}
            >
              <CustomCheckbox
                checked={isExpandAllChecked}
                className="audit-table__expand-all-checkbox"
              />
              <span className="audit-table__expand-all-label">
                Expand all
              </span>
            </label>
          </div>
        </TableCell>
        <TableCell
          colSpan={inventoryColumnCount}
          className={
            hasPosType
              ? withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: 1, totalRows: headerRowCount, totalCols: totalColumnCount }))
              : getCompleteCellClass({ rowIdx: 0, colIdx: 1, totalRows: headerRowCount, totalCols: totalColumnCount })
          }
        >
          {renderHeaderTitle('Scratcher', true)}
        </TableCell>

        {showNetVariance && isBothConnected && (
          <>
            <TableCell
              rowSpan={2}
              colSpan={3}
              className={withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: headerRowCount, totalCols: totalColumnCount }))}
            >
              <p className="table-header-text text-align-center">Lotto</p>
            </TableCell>
            <TableCell
              rowSpan={2}
              colSpan={4}
              className={withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex + 3, totalRows: headerRowCount, totalCols: totalColumnCount }))}
            >
              <p className="table-header-text text-align-center">Payouts</p>
            </TableCell>
          </>
        )}

        {showNetVariance && isBothNotConnected && (
          <TableCell
            rowSpan={2}
            colSpan={2}
            className={getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: headerRowCount, totalCols: totalColumnCount })}
          >
            <p className="table-header-text text-align-center">Scratcher Summary</p>
          </TableCell>
        )}

        {showNetVariance && isLottoConnectedOnly && (
          <>
            <TableCell
              rowSpan={2}
              colSpan={2}
              className={getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: headerRowCount, totalCols: totalColumnCount })}
            >
              <p className="table-header-text text-align-center">Scratcher Summary</p>
            </TableCell>
            <TableCell
              rowSpan={2}
              colSpan={2}
              className={getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex + 2, totalRows: headerRowCount, totalCols: totalColumnCount })}
            >
              <p className="table-header-text text-align-center">Lotto Summary</p>
            </TableCell>
          </>
        )}

        {showNetVariance && isPOSConnectedOnly && (
          <>
            <TableCell
              rowSpan={2}
              colSpan={3}
              className={withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: headerRowCount, totalCols: totalColumnCount }))}
            >
              <p className="table-header-text text-align-center">Lotto</p>
            </TableCell>
            <TableCell
              rowSpan={2}
              colSpan={4}
              className={withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex + 3, totalRows: headerRowCount, totalCols: totalColumnCount }))}
            >
              <p className="table-header-text text-align-center">Payouts</p>
            </TableCell>
          </>
        )}
        {showNetVariance && (
          <TableCell
            rowSpan={3}
            className={getCompleteCellClass({ rowIdx: 0, colIdx: reconciliationColumnIndex, totalRows: headerRowCount, totalCols: totalColumnCount })}
            style={{ ...AUDIT_TABLE_COLUMN_STYLES.metric, textAlign: 'right' }}
          >
            <p className="table-header-text text-align-center">Net Variance</p>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        {columnsBeforeBackstock.map((column, index) => (
          renderTableCell(column, index, 2)
        ))}
        {columnsBeforeCounter.map((column, index) => (
          renderTableCell(column, index, 2)
        ))}
        {backstockColumns.length > 0 && (
          <TableCell
            colSpan={backstockColumns.length}
            className={getCompleteCellClass({
              rowIdx: 0,
              colIdx: backstockStartIndex + 1,
              totalRows: headerRowCount,
              totalCols: totalColumnCount,
              borderColumns: conditionalBorderColumns
            }) + ' audit-table__inventory-group-header--backstock'}
            style={inventoryGroupHeaderStyles.backstock.cell}
          >
            {renderGroupLabel('Backstock', 'backstock')}
          </TableCell>
        )}
        {counterColumns.length > 0 && (
          <TableCell
            colSpan={counterColumns.length}
            className={getCompleteCellClass({
              rowIdx: 0,
              colIdx: counterStartIndex + 1,
              totalRows: headerRowCount,
              totalCols: totalColumnCount,
              borderColumns: conditionalBorderColumns
            })}
            style={inventoryGroupHeaderStyles.counter.cell}
          >
            {renderGroupLabel('Counter', 'counter')}
          </TableCell>
        )}
        {columnsAfterCounter.map((column, index) => (
          renderTableCell(column, counterStartIndex + counterColumns.length + index, 2)
        ))}
      </TableRow>
      <TableRow>
        {backstockColumns.map((column, index) => (
          renderTableCell(column, backstockStartIndex + index, undefined, { isBackstockColumnHeading: true })
        ))}
        {counterColumns.map((column, index) => (
          renderTableCell(column, counterStartIndex + index)
        ))}
        {summaryColumns.map((column, index) => (
          renderTableCell(column, inventoryColumnCount + index)
        ))}
      </TableRow>
    </>
  );


  return (
    <TableHead>
      {hasGroupedInventoryHeader && renderBackstockAccountingHeader()}
      {!hasGroupedInventoryHeader && (
        <>
      <TableRow>
        <TableCell
          rowSpan={2}
          className={getCompleteCellClass({ rowIdx: 0, colIdx: 0, totalRows: 1, totalCols: totalColumnCount })}
          style={{ ...dateColumnStyle, cursor: 'pointer' }}
          aria-sort={dateSortDirection === 'asc' ? 'ascending' : 'descending'}
          onClick={() => onDateSortChange?.()}
        >
          <div className="audit-table__date-header">
            <p className="table-header-text">
              <TableSortLabel
                active
                direction={dateSortDirection}
                hideSortIcon={false}
              >
                Date
              </TableSortLabel>
            </p>
            <label
              className="audit-table__expand-all-control"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onExpandAllChange(!isExpandAllChecked);
              }}
            >
              <CustomCheckbox
                checked={isExpandAllChecked}
                className="audit-table__expand-all-checkbox"
              />
              <span className="audit-table__expand-all-label">
                Expand all
              </span>
            </label>
          </div>
        </TableCell>
        <TableCell
          colSpan={inventoryColumnCount}
          className={
            hasPosType
              ? withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: 1, totalRows: 2, totalCols: totalColumnCount }))
              : getCompleteCellClass({ rowIdx: 0, colIdx: 1, totalRows: 2, totalCols: totalColumnCount })
          }
        >
          {renderHeaderTitle('Scratcher', true)}
        </TableCell>

        {isBothConnected && (
          <>
            <Header
              title="Lotto"
              cellStyle={{ className: withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: 2, totalCols: totalColumnCount })) }}
              colSpan={3}
            />
            <Header
              title="Payouts"
              cellStyle={{ className: withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex + 3, totalRows: 2, totalCols: totalColumnCount })) }}
              colSpan={4}
            />
          </>
        )}

        {isBothNotConnected && (
          <Header
            title="Scratcher Summary"
            cellStyle={{ className: getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: 2, totalCols: totalColumnCount }) }}
            colSpan={2}
          />
        )}

        {isLottoConnectedOnly && (
          <>
            <Header
              title="Scratcher Summary"
              cellStyle={{ className: getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: 2, totalCols: totalColumnCount }) }}
              colSpan={2}
            />
            <Header
              title="Lotto Summary"
              cellStyle={{ className: getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex + 2, totalRows: 2, totalCols: totalColumnCount }) }}
              colSpan={2}
            />
          </>
        )}

        {isPOSConnectedOnly && (
          <>
            <Header
              title="Lotto"
              cellStyle={{ className: withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex, totalRows: 2, totalCols: totalColumnCount })) }}
              colSpan={3}
            />
            <Header
              title="Payouts"
              cellStyle={{ className: withVarianceDollarRightBorder(getCompleteCellClass({ rowIdx: 0, colIdx: summaryStartColumnIndex + 3, totalRows: 2, totalCols: totalColumnCount })) }}
              colSpan={4}
            />
          </>
        )}
        {showNetVariance && (
          <TableCell
            rowSpan={2}
            className={getCompleteCellClass({ rowIdx: 0, colIdx: reconciliationColumnIndex, totalRows: 1, totalCols: totalColumnCount })}
            style={{ ...AUDIT_TABLE_COLUMN_STYLES.metric, textAlign: 'right' }}
          >
            <p className="table-header-text text-align-center">Net Variance</p>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        {columnConfig.map((column, index) => renderTableCell(column, index))}
      </TableRow>
        </>
      )}
    </TableHead>
  );
};

export default AuditTableHeader; 
